import { z } from 'zod';
import { OrganizationUserRole } from 'libs/end-to-end/src/types/users/organization-user-roles';

export const SSOEnabledSummary = z.object({
  ssoEnabled: z.literal(true),
});

export const SSODisabledSummary = z.object({
  ssoEnabled: z.literal(false),
});

export const SSOSummary = z.union([SSOEnabledSummary, SSODisabledSummary]);

export const SSOIdentityProviderType = z.union([
  z.literal('saml'),
  z.literal('oidc'),
]);

export const SSOIdentityProviderOidc = z.object({
  type: z.literal('oidc'),
  id: z.string().min(1),
  name: z.string().min(1),
  organizationId: z.string().min(1),
  publicSsoIdentifier: z.string().min(1),
  clientId: z.string().min(1),
  issuerUrl: z.string().min(1),
  scopes: z.string().min(1),
  emailClaimMapping: z.string(),
  groupsClaimMapping: z.string().optional(),
});

export const SSOIdentityProviderSaml = z.object({
  type: z.literal('saml'),
  id: z.string().min(1),
  name: z.string().min(1),
  organizationId: z.string().min(1),
  publicSsoIdentifier: z.string().min(1),
  metadataUrl: z.string().min(1),
  emailClaimMapping: z.string().min(1),
  groupsClaimMapping: z.string().optional(),
});

export const SSOIdentityProvider = z.union([
  SSOIdentityProviderOidc,
  SSOIdentityProviderSaml,
]);

export type SSOIdentityProviderOidc = z.infer<typeof SSOIdentityProviderOidc>;
export type SSOIdentityProviderSaml = z.infer<typeof SSOIdentityProviderSaml>;
export type SSOIdentityProvider = z.infer<typeof SSOIdentityProvider>;

export const SSOIdentityProviderBasic = z.object({
  type: SSOIdentityProviderType,
  name: z.string(),
  id: z.string(),
});
export type SSOIdentityProviderBasic = z.infer<typeof SSOIdentityProviderBasic>;

export const SSOIdentityProviderList = z.array(SSOIdentityProviderBasic);
export type SSOIdentityProviderList = z.infer<typeof SSOIdentityProviderList>;

export const CreateSsoIdentityProviderDataOidc = SSOIdentityProviderOidc.extend(
  {
    clientSecret: z.string(),
  },
).omit({ id: true, publicSsoIdentifier: true });

export const CreateSsoIdentityProviderDataSaml = SSOIdentityProviderSaml.omit({
  id: true,
  publicSsoIdentifier: true,
});

export const CreateSsoIdentityProviderData = z.union([
  CreateSsoIdentityProviderDataOidc,
  CreateSsoIdentityProviderDataSaml,
]);
export type CreateSsoIdentityProviderDataOidc = z.infer<
  typeof CreateSsoIdentityProviderDataOidc
>;

export type CreateSsoIdentityProviderDataSaml = z.infer<
  typeof CreateSsoIdentityProviderDataSaml
>;

export type CreateSsoIdentityProviderData = z.infer<
  typeof CreateSsoIdentityProviderData
>;

export const UpdateSsoIdentityProviderDataOidc = SSOIdentityProviderOidc.extend(
  {
    clientSecret: z.string().optional(),
  },
).omit({
  name: true,
  id: true,
  organizationId: true,
  publicSsoIdentifier: true,
});

export const UpdateSsoIdentityProviderDataSaml = SSOIdentityProviderSaml.omit({
  name: true,
  id: true,
  organizationId: true,
  publicSsoIdentifier: true,
});

export const UpdateSsoIdentityProviderData = z.union([
  UpdateSsoIdentityProviderDataOidc,
  UpdateSsoIdentityProviderDataSaml,
]);

export type UpdateSsoIdentityProviderDataOidc = z.infer<
  typeof UpdateSsoIdentityProviderDataOidc
>;
export type UpdateSsoIdentityProviderDataSaml = z.infer<
  typeof UpdateSsoIdentityProviderDataSaml
>;
export type UpdateSsoIdentityProviderData = z.infer<
  typeof UpdateSsoIdentityProviderData
>;

export type SSOSummary = z.infer<typeof SSOSummary>;

export const SSORoleMappingComparisonType = z.union([
  z.literal('equals'),
  z.literal('contains'),
]);

export type SSORoleMappingComparisonType = z.infer<
  typeof SSORoleMappingComparisonType
>;

export const SSORoleMapping = z.object({
  s2ClaimName: z.string().min(1),
  propertyValue: z.string().min(1),
  comparisonType: SSORoleMappingComparisonType,
  organizationRole: OrganizationUserRole.optional(),
  isSuperAdmin: z.boolean(),
});
export type SSORoleMapping = z.infer<typeof SSORoleMapping>;

export const SSORoleMappingSummary = SSORoleMapping.omit({
  isSuperAdmin: true,
  organizationRole: true,
});
export type SSORoleMappingSummary = z.infer<typeof SSORoleMappingSummary>;

export const SSORoleMappingsSummary = z.object({
  ssoRoleMappings: z.array(SSORoleMappingSummary),
});
export type SSORoleMappingsSummary = z.infer<typeof SSORoleMappingsSummary>;
